import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import { useNavigate, useParams } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import 'dayjs/locale/uk';
import { fetchStudent, fetchStudentSummary } from '../../api/students';
import { createLesson, deleteLesson, fetchLessons, updateLesson, uploadHomeworkFile, deleteHomeworkFile } from '../../api/lessons';
import { fetchPayments, createPayment, updatePayment, deletePayment } from '../../api/payments';
import LessonFormDialog from '../../components/forms/LessonFormDialog';
import ConfirmDialog from '../../components/feedback/ConfirmDialog';
import PaymentFormDialog from '../../components/payment/PaymentFormDialog';
import PaymentHistory from '../../components/payment/PaymentHistory';
import StudentInfoCard from '../../components/student/StudentInfoCard';
import JournalHeader from '../../components/student/JournalHeader';
import PaymentBadge from '../../components/payment/PaymentBadge';
import LessonScoreCell from '../../components/lessons/LessonScoreCell';
import LessonFormatBadge from '../../components/lessons/LessonFormatBadge';
import LessonFilters from '../../components/lessons/LessonFilters';
import LessonPerformanceChart from '../../components/lessons/LessonPerformanceChart';
import LessonCardMobile from '../../components/lessons/LessonCardMobile';
import HomeworkCell from '../../components/lessons/HomeworkCell';
import ExpandableText from '../../components/common/ExpandableText';
import AssessmentTable from '../../components/lessons/AssessmentTable';
import { enqueueSnackbar } from 'notistack';
import DownloadIcon from '@mui/icons-material/Download';
import { exportLessonsToExcel } from '../../utils/exportLessonsToExcel';

dayjs.locale('uk');

const B = { primary: '#4357AD', dark: '#1E2A4A' };
const cardSx = { borderRadius: 4, boxShadow: '0 1px 6px rgba(0,0,0,0.04)', bgcolor: '#fff' };
const isIncomplete = (lesson) => !lesson.topic || (lesson.homeworkScore == null && lesson.activityScore == null);

const AdminStudentJournalPage = () => {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isNarrow = useMediaQuery('(max-width:900px)');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [confirmState, setConfirmState] = useState(null);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [paymentConfirmState, setPaymentConfirmState] = useState(null);
  const [topicFilter, setTopicFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(null);

  const studentQuery = useQuery({
    queryKey: ['student', studentId],
    queryFn: () => fetchStudent(studentId),
    enabled: Boolean(studentId),
  });

  const student = studentQuery.data;

  const lessonsQuery = useQuery({
    queryKey: ['lessons', studentId],
    queryFn: () => fetchLessons(studentId),
    enabled: Boolean(studentId),
  });

  const summaryQuery = useQuery({
    queryKey: ['student-summary', studentId],
    queryFn: () => fetchStudentSummary(studentId),
    enabled: Boolean(studentId),
  });

  const paymentsQuery = useQuery({
    queryKey: ['payments', studentId],
    queryFn: () => fetchPayments(studentId),
    enabled: Boolean(studentId),
  });

  const filteredLessons = useMemo(() => {
    const lessons = lessonsQuery.data || [];
    return lessons
      .filter((lesson) => {
        const matchesTopic = topicFilter
          ? (lesson.topic || '').toLowerCase().includes(topicFilter.toLowerCase())
          : true;
        const lessonDate = dayjs(lesson.date).format('YYYY-MM-DD');
        const matchesDateFrom = dateFrom ? lessonDate >= dateFrom : true;
        const matchesDateTo = dateTo ? lessonDate <= dateTo : true;
        return matchesTopic && matchesDateFrom && matchesDateTo;
      })
      .sort((a, b) => dayjs(b.date).valueOf() - dayjs(a.date).valueOf());
  }, [lessonsQuery.data, topicFilter, dateFrom, dateTo]);

  const availableMonths = useMemo(() => {
    const months = new Map();
    (lessonsQuery.data || []).forEach((l) => {
      const d = dayjs(l.date);
      const key = d.format('YYYY-MM');
      if (!months.has(key)) {
        months.set(key, { key, label: d.format('MMM YYYY') });
      }
    });
    return [...months.values()].sort((a, b) => a.key.localeCompare(b.key));
  }, [lessonsQuery.data]);

  const displayedLessons = useMemo(() => {
    if (!selectedMonth) return filteredLessons;
    return filteredLessons.filter((l) => dayjs(l.date).format('YYYY-MM') === selectedMonth);
  }, [filteredLessons, selectedMonth]);

  const invalidateStudentData = () => {
    queryClient.invalidateQueries({ queryKey: ['payments', studentId] });
    queryClient.invalidateQueries({ queryKey: ['lessons', studentId] });
    queryClient.invalidateQueries({ queryKey: ['student-summary', studentId] });
  };

  const createMutation = useMutation({
    mutationFn: (payload) => createLesson({ ...payload, studentId: Number(studentId) }),
    onSuccess: () => {
      enqueueSnackbar('Урок додано', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['lessons', studentId] });
      queryClient.invalidateQueries({ queryKey: ['student-summary', studentId] });
    },
    onError: () => enqueueSnackbar('Не вдалося додати урок', { variant: 'error' }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }) => updateLesson(id, payload),
    onSuccess: () => {
      enqueueSnackbar('Урок оновлено', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['lessons', studentId] });
      queryClient.invalidateQueries({ queryKey: ['student-summary', studentId] });
    },
    onError: () => enqueueSnackbar('Не вдалося оновити урок', { variant: 'error' }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => deleteLesson(id),
    onSuccess: () => {
      enqueueSnackbar('Урок видалено', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['lessons', studentId] });
      queryClient.invalidateQueries({ queryKey: ['student-summary', studentId] });
    },
    onError: () => enqueueSnackbar('Не вдалося видалити урок', { variant: 'error' }),
  });

  const paymentMutation = useMutation({
    mutationFn: createPayment,
    onSuccess: () => {
      enqueueSnackbar('Оплату додано', { variant: 'success' });
      invalidateStudentData();
    },
    onError: () => enqueueSnackbar('Не вдалося додати оплату', { variant: 'error' }),
  });

  const updatePaymentMutation = useMutation({
    mutationFn: ({ id, payload }) => updatePayment(id, payload),
    onSuccess: () => {
      enqueueSnackbar('Оплату оновлено', { variant: 'success' });
      invalidateStudentData();
    },
    onError: () => enqueueSnackbar('Не вдалося оновити оплату', { variant: 'error' }),
  });

  const deletePaymentMutation = useMutation({
    mutationFn: (id) => deletePayment(id),
    onSuccess: () => {
      enqueueSnackbar('Оплату видалено', { variant: 'success' });
      invalidateStudentData();
    },
    onError: () => enqueueSnackbar('Не вдалося видалити оплату', { variant: 'error' }),
  });

  const handlePaymentSubmit = async (values) => {
    if (editingPayment) {
      await updatePaymentMutation.mutateAsync({ id: editingPayment.id, payload: values });
    } else {
      await paymentMutation.mutateAsync({ ...values, studentId: Number(studentId) });
    }
    setEditingPayment(null);
  };

  const syncHomeworkFiles = async (lessonId, pendingFiles, removedFileIds) => {
    if (!pendingFiles.length && !removedFileIds.length) return;
    for (const fileId of removedFileIds) {
      try {
        await deleteHomeworkFile(lessonId, fileId);
      } catch {
        enqueueSnackbar('Не вдалося видалити файл', { variant: 'error' });
      }
    }
    for (const file of pendingFiles) {
      try {
        await uploadHomeworkFile(lessonId, file);
      } catch (err) {
        enqueueSnackbar(`Не вдалося завантажити «${file.name}»: ${err.response?.data?.message || 'помилка'}`, { variant: 'error' });
      }
    }
    // мутація інвалідовує кеш ще до файлових операцій — повторюємо після них
    queryClient.invalidateQueries({ queryKey: ['lessons', studentId] });
  };

  const handleSubmit = async (values, fileOps = {}) => {
    const { pendingFiles = [], removedFileIds = [] } = fileOps;
    if (selectedLesson) {
      await updateMutation.mutateAsync({ id: selectedLesson.id, payload: values });
      await syncHomeworkFiles(selectedLesson.id, pendingFiles, removedFileIds);
    } else {
      const created = await createMutation.mutateAsync(values);
      await syncHomeworkFiles(created.id, pendingFiles, []);
    }
  };

  if (studentQuery.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!student) {
    return (
      <Stack spacing={2}>
        <Typography>Учня не знайдено.</Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/students')}>
          Повернутися до списку
        </Button>
      </Stack>
    );
  }

  return (
    <Stack spacing={3}>
      <JournalHeader
        student={student}
        onBack={() => navigate('/admin/students')}
        action={
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            sx={{ bgcolor: B.primary, '&:hover': { bgcolor: '#3648A0' }, borderRadius: 3, px: 3, textTransform: 'none', fontWeight: 600 }}
            onClick={() => { setSelectedLesson(null); setDialogOpen(true); }}
          >
            Додати урок
          </Button>
        }
      />

      <StudentInfoCard student={student} summary={summaryQuery.data} />

      <PaymentHistory
        payments={paymentsQuery.data}
        isLoading={paymentsQuery.isLoading}
        onAddClick={() => {
          setEditingPayment(null);
          setPaymentDialogOpen(true);
        }}
        onEdit={(payment) => {
          setEditingPayment(payment);
          setPaymentDialogOpen(true);
        }}
        onDelete={(payment) => setPaymentConfirmState(payment)}
        canManage
      />

      <Card sx={{ ...cardSx, overflow: 'hidden', border: `2px solid ${B.primary}` }}>
        {lessonsQuery.isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: B.primary }} /></Box>
        ) : (
          <>
          <Box sx={{ px: { xs: 2, md: 3 }, py: 2, bgcolor: '#F8F7FF', borderBottom: '2px solid #EDE9FE', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: { md: 'center' }, justifyContent: 'space-between' }}>
            <LessonFilters topicFilter={topicFilter} onTopicChange={setTopicFilter} dateFrom={dateFrom} onDateFromChange={setDateFrom} dateTo={dateTo} onDateToChange={setDateTo} />
            <Button variant="outlined" startIcon={<DownloadIcon />} disabled={!filteredLessons.length} onClick={() => exportLessonsToExcel(filteredLessons, student.name)}
              sx={{ borderColor: '#EDE9FE', color: B.primary, borderRadius: 3, textTransform: 'none', fontWeight: 600, flexShrink: 0, '&:hover': { borderColor: B.primary, bgcolor: '#EDE9FE' } }}>
              Експорт
            </Button>
          </Box>
          {availableMonths.length > 1 && (
            <Box sx={{ px: { xs: 2, md: 3 }, py: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap', borderBottom: '1px solid #EDE9FE', bgcolor: '#fff' }}>
              <Chip label="Усі" size="small" onClick={() => setSelectedMonth(null)}
                sx={{ fontWeight: 600, fontSize: 12, cursor: 'pointer', bgcolor: !selectedMonth ? B.primary : '#F3F0FF', color: !selectedMonth ? '#fff' : '#8891A5', '&:hover': { bgcolor: !selectedMonth ? '#3648A0' : '#EDE9FE' } }} />
              {availableMonths.map((m) => (
                <Chip key={m.key} label={m.label} size="small" onClick={() => setSelectedMonth(m.key)}
                  sx={{ fontWeight: 600, fontSize: 12, cursor: 'pointer', textTransform: 'capitalize', bgcolor: selectedMonth === m.key ? B.primary : '#F3F0FF', color: selectedMonth === m.key ? '#fff' : '#8891A5', '&:hover': { bgcolor: selectedMonth === m.key ? '#3648A0' : '#EDE9FE' } }} />
              ))}
            </Box>
          )}
          {isNarrow ? (
            <Box>
              {(() => {
                let activeCount = displayedLessons.filter((l) => !l.deleted).length;
                return displayedLessons.map((lesson) => {
                  let rowNum = null;
                  if (!lesson.deleted) {
                    rowNum = activeCount;
                    activeCount -= 1;
                  }
                  return (
                    <LessonCardMobile
                      key={lesson.id}
                      lesson={lesson}
                      rowNum={rowNum}
                      metaSlot={
                        <Stack spacing={0.25}>
                          {lesson.createdAt && (
                            <Typography variant="caption" sx={{ color: '#B0B8C9', fontSize: 10 }}>
                              Додано: {dayjs(lesson.createdAt).format('DD.MM.YYYY HH:mm')}
                            </Typography>
                          )}
                          {lesson.updatedAt && lesson.updatedAt !== lesson.createdAt && (
                            <Typography variant="caption" sx={{ color: '#B0B8C9', fontSize: 10 }}>
                              Змінено: {dayjs(lesson.updatedAt).format('DD.MM.YYYY HH:mm')}
                            </Typography>
                          )}
                          {lesson.deleted && (
                            <Chip label={`Видалено${lesson.deletedAt ? ': ' + dayjs(lesson.deletedAt).format('DD.MM HH:mm') : ''}`} size="small"
                              sx={{ alignSelf: 'flex-start', bgcolor: '#FEE2E2', color: '#DC2626', fontWeight: 600, fontSize: 10, height: 20 }} />
                          )}
                        </Stack>
                      }
                      statusSlot={
                        lesson.deleted ? null : (
                          <PaymentBadge paid={lesson.paid} size="md" onToggle={() => updateMutation.mutate({ id: lesson.id, payload: { paid: !lesson.paid } })} />
                        )
                      }
                      actionsSlot={
                        lesson.deleted ? (
                          <Typography variant="caption" sx={{ color: '#DC2626', fontStyle: 'italic' }}>Архів</Typography>
                        ) : (
                          <Stack direction="row" spacing={0.25}>
                            <IconButton size="small" aria-label="Редагувати" onClick={() => { setSelectedLesson(lesson); setDialogOpen(true); }} sx={{ color: '#8891A5', '&:hover': { color: '#fff', bgcolor: B.primary } }}><EditIcon fontSize="small" /></IconButton>
                            <IconButton size="small" aria-label="Видалити" onClick={() => setConfirmState(lesson)} sx={{ color: '#8891A5', '&:hover': { color: '#fff', bgcolor: '#EF4444' } }}><DeleteIcon fontSize="small" /></IconButton>
                          </Stack>
                        )
                      }
                    />
                  );
                });
              })()}
              {!displayedLessons.length && (
                <Box sx={{ py: 6, textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ color: '#8891A5' }}>Немає уроків за заданими критеріями.</Typography>
                </Box>
              )}
            </Box>
          ) : (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: 1080 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: '#F8F7FF', '& th': { color: B.primary, fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.8, textAlign: 'center', borderBottom: '2px solid #EDE9FE', borderRight: '1px solid #EDE9FE', py: 1.8, px: { xs: 1, md: 2.5 }, '&:last-child': { borderRight: 'none' } } }}>
                  <TableCell sx={{ width: 50 }}>#</TableCell>
                  <TableCell>Дата</TableCell>
                  <TableCell>Тема</TableCell>
                  <TableCell>Примітки</TableCell>
                  <TableCell>Домашнє завдання</TableCell>
                  <TableCell align="center">Оцінка</TableCell>
                  <TableCell align="center">Оплата</TableCell>
                  <TableCell align="right">Дії</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(() => {
                  let activeCount = displayedLessons.filter((l) => !l.deleted).length;
                  return displayedLessons.map((lesson, idx) => {
                  const incomplete = isIncomplete(lesson);
                  let rowNum = null;
                  if (!lesson.deleted) {
                    rowNum = activeCount;
                    activeCount -= 1;
                  }
                  return (
                    <TableRow key={lesson.id} sx={{ transition: 'all 0.15s', bgcolor: lesson.deleted ? '#FEF2F2' : (idx % 2 === 0 ? '#fff' : '#FAFAFF'), opacity: lesson.deleted ? 0.7 : 1, '&:hover': { bgcolor: lesson.deleted ? '#FEE2E2' : '#EDE9FE' }, '& td': { py: { xs: 1, md: 2 }, px: { xs: 1, md: 2.5 }, borderBottom: '1px solid #F1F0FB', borderRight: '1px solid #F1F0FB', '&:last-child': { borderRight: 'none' } } }}>
                      <TableCell>
                        <Box sx={{ width: 32, height: 32, borderRadius: 2, bgcolor: lesson.deleted ? '#EF4444' : B.primary, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13 }}>
                          {lesson.deleted ? '—' : String(rowNum).padStart(2, '0')}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Stack>
                          <Typography variant="body2" fontWeight={700} sx={{ color: B.dark }}>{dayjs(lesson.date).format('DD.MM.YYYY')}</Typography>
                          <Typography variant="caption" sx={{ color: '#8891A5' }}>
                            {dayjs(lesson.date).format('dddd')}
                            {lesson.scheduleStartTime && (
                              <>, {dayjs(lesson.scheduleStartTime).format('HH:mm')}</>
                            )}
                          </Typography>
                          {lesson.createdAt && (
                            <Typography variant="caption" sx={{ color: '#B0B8C9', fontSize: 10, mt: 0.5 }}>
                              Додано: {dayjs(lesson.createdAt).format('DD.MM.YYYY HH:mm')}
                            </Typography>
                          )}
                          {lesson.updatedAt && lesson.updatedAt !== lesson.createdAt && (
                            <Typography variant="caption" sx={{ color: '#B0B8C9', fontSize: 10 }}>
                              Змінено: {dayjs(lesson.updatedAt).format('DD.MM.YYYY HH:mm')}
                            </Typography>
                          )}
                          {lesson.deleted && (
                            <Chip label={`Видалено${lesson.deletedAt ? ': ' + dayjs(lesson.deletedAt).format('DD.MM HH:mm') : ''}`} size="small"
                              sx={{ mt: 0.5, bgcolor: '#FEE2E2', color: '#DC2626', fontWeight: 600, fontSize: 10, height: 20 }} />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography lang="uk" variant="body2" fontWeight={700} sx={{ color: lesson.topic ? B.dark : '#8891A5', fontStyle: lesson.topic ? 'normal' : 'italic', hyphens: 'auto', overflowWrap: 'break-word', wordBreak: 'normal' }}>
                          {lesson.topic || 'Не заповнено'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 260, minWidth: 200 }}>
                        {lesson.notes ? (
                          <ExpandableText lines={4} sx={{ bgcolor: '#F8F7FF', border: '1px solid #EDE9FE', borderRadius: 2, px: 1.5, py: 0.75 }}>
                            {lesson.notes}
                          </ExpandableText>
                        ) : (
                          <Typography variant="body2" sx={{ color: '#C4B5FD', fontStyle: 'italic' }}>Без приміток</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 260, minWidth: 200 }}>
                        <HomeworkCell lesson={lesson} />
                      </TableCell>
                      <TableCell align="center"><LessonScoreCell lesson={lesson} /></TableCell>
                      <TableCell align="center">
                        {lesson.deleted ? (
                          <Typography variant="caption" sx={{ color: '#94A3B8', fontStyle: 'italic' }}>—</Typography>
                        ) : (
                          <PaymentBadge paid={lesson.paid} onToggle={() => updateMutation.mutate({ id: lesson.id, payload: { paid: !lesson.paid } })} />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {!lesson.deleted ? (
                          <Stack direction="row" spacing={0.25} justifyContent="flex-end">
                            <Tooltip title="Редагувати"><IconButton size="small" onClick={() => { setSelectedLesson(lesson); setDialogOpen(true); }} sx={{ color: '#8891A5', '&:hover': { color: '#fff', bgcolor: B.primary } }}><EditIcon fontSize="small" /></IconButton></Tooltip>
                            <Tooltip title="Видалити"><IconButton size="small" onClick={() => setConfirmState(lesson)} sx={{ color: '#8891A5', '&:hover': { color: '#fff', bgcolor: '#EF4444' } }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                          </Stack>
                        ) : (
                          <Typography variant="caption" sx={{ color: '#DC2626', fontStyle: 'italic' }}>Архів</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                });
                })()}
                {!displayedLessons.length && (
                  <TableRow><TableCell colSpan={8} align="center" sx={{ py: 6, borderBottom: 'none' }}><Typography variant="body2" sx={{ color: '#8891A5' }}>Немає уроків за заданими критеріями.</Typography></TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          )}
          </>
        )}
      </Card>

      <AssessmentTable studentId={studentId} canEdit />

      <LessonPerformanceChart lessons={filteredLessons} />

      <PaymentFormDialog
        open={paymentDialogOpen}
        onClose={() => {
          setPaymentDialogOpen(false);
          setEditingPayment(null);
        }}
        onSubmit={handlePaymentSubmit}
        initialValues={
          editingPayment
            ? {
                paymentDate: dayjs(editingPayment.paymentDate).format('YYYY-MM-DD'),
                amount: editingPayment.amount,
                lessonType: editingPayment.lessonType,
                paymentMethod: editingPayment.paymentMethod,
                lessonsCovered: editingPayment.lessonsCovered,
              }
            : undefined
        }
        title={editingPayment ? 'Редагувати оплату' : 'Додати оплату'}
        submitLabel={editingPayment ? 'Оновити' : 'Зберегти'}
      />

      <LessonFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSubmit={handleSubmit}
        initialValues={{
          date: selectedLesson?.date || dayjs().format('YYYY-MM-DD'),
          topic: selectedLesson?.topic || '',
          notes: selectedLesson?.notes || '',
          homeworkScore: selectedLesson?.homeworkScore ?? '',
          activityScore: selectedLesson?.activityScore || '',
          behaviorScore: selectedLesson?.behaviorScore || '',
          comprehensionScore: selectedLesson?.comprehensionScore || '',
          homeworkTask: selectedLesson?.homeworkTask || '',
          homeworkFiles: selectedLesson?.homeworkFiles || [],
          paid: selectedLesson?.paid || false,
        }}
        enablePayment
        title={selectedLesson ? 'Редагувати урок' : 'Новий урок'}
      />

      <ConfirmDialog
        open={Boolean(confirmState)}
        title="Видалити урок?"
        description="Цю дію неможливо скасувати."
        onCancel={() => setConfirmState(null)}
        onConfirm={async () => {
          await deleteMutation.mutateAsync(confirmState.id);
          setConfirmState(null);
        }}
      />

      <ConfirmDialog
        open={Boolean(paymentConfirmState)}
        title="Видалити оплату?"
        description="Цю дію неможливо скасувати."
        onCancel={() => setPaymentConfirmState(null)}
        onConfirm={async () => {
          await deletePaymentMutation.mutateAsync(paymentConfirmState.id);
          setPaymentConfirmState(null);
        }}
      />
    </Stack>
  );
};

export default AdminStudentJournalPage;

